// Storage management utility to handle localStorage quota issues
export class StorageManager {
  static STORAGE_KEY = 'saltfishy_fish_inventory';
  static MAX_STORAGE_SIZE = 4 * 1024 * 1024; // 4MB limit (conservative estimate)
  static COMPRESSION_THRESHOLD = 2 * 1024 * 1024; // 2MB threshold for compression

  // Check if data fits in storage
  static canStore(data) {
    const serializedData = JSON.stringify(data);
    return serializedData.length <= this.MAX_STORAGE_SIZE;
  }

  // Get storage usage info
  static getStorageInfo(data) {
    const serializedData = JSON.stringify(data);
    const sizeInBytes = serializedData.length;
    const sizeInKB = (sizeInBytes / 1024).toFixed(1);
    const percentUsed = ((sizeInBytes / this.MAX_STORAGE_SIZE) * 100).toFixed(1);
    
    return {
      sizeInBytes,
      sizeInKB,
      percentUsed,
      isNearLimit: sizeInBytes > this.COMPRESSION_THRESHOLD,
      isOverLimit: sizeInBytes > this.MAX_STORAGE_SIZE
    };
  }

  // Compress fish data by removing large/unnecessary fields
  static compressFishData(fishList) {
    return fishList.map(fish => {
      const compressed = { ...fish };
      
      // Truncate very long descriptions
      if (compressed.description && compressed.description.length > 200) {
        compressed.description = compressed.description.substring(0, 197) + '...';
      }
      
      // Convert very long image URLs to placeholder
      if (compressed.image_url && compressed.image_url.length > 500) {
        console.warn(`Fish "${fish.name}" has very long image URL, replacing with placeholder`);
        compressed.image_url = null;
      }
      
      // Remove any undefined or null values to save space
      Object.keys(compressed).forEach(key => {
        if (compressed[key] === undefined || compressed[key] === null) {
          delete compressed[key];
        }
      });
      
      return compressed;
    });
  }

  // Safely save fish data to localStorage
  static saveFishData(fishList) {
    try {
      let dataToSave = fishList;
      const storageInfo = this.getStorageInfo(dataToSave);
      
      // If data is too large, try compression
      if (storageInfo.isOverLimit) {
        console.warn('Fish data is too large for localStorage, attempting compression...');
        dataToSave = this.compressFishData(fishList);
        
        const compressedInfo = this.getStorageInfo(dataToSave);
        if (compressedInfo.isOverLimit) {
          throw new Error(`Data still too large after compression: ${compressedInfo.sizeInKB}KB`);
        }
        console.log(`Compressed data from ${storageInfo.sizeInKB}KB to ${compressedInfo.sizeInKB}KB`);
      }
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(dataToSave));
      return { success: true, storageInfo: this.getStorageInfo(dataToSave) };
      
    } catch (error) {
      console.error('Failed to save fish data:', error);
      
      // If storage fails, try to free up space by removing old data first
      if (error.name === 'QuotaExceededError' || error.message.includes('quota')) {
        try {
          this.clearOldData();
          // Try saving compressed data again
          const compressedData = this.compressFishData(fishList);
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(compressedData));
          return { success: true, storageInfo: this.getStorageInfo(compressedData), compressed: true };
        } catch (secondError) {
          return { 
            success: false, 
            error: 'Storage quota exceeded even after cleanup', 
            suggestion: 'Please reduce the number of fish or contact support'
          };
        }
      }
      
      return { success: false, error: error.message };
    }
  }

  // Load fish data from localStorage
  static loadFishData() {
    try {
      const savedData = localStorage.getItem(this.STORAGE_KEY);
      if (savedData) {
        return JSON.parse(savedData);
      }
      return null;
    } catch (error) {
      console.error('Failed to load fish data:', error);
      // If data is corrupted, clear it
      localStorage.removeItem(this.STORAGE_KEY);
      return null;
    }
  }

  // Clear old/unnecessary data from localStorage
  static clearOldData() {
    // Remove any old keys that might be taking up space
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes('old_') || 
        key.includes('backup_') || 
        key.includes('temp_') ||
        key.startsWith('react-') ||
        key.startsWith('debug_')
      )) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`Removed old localStorage key: ${key}`);
    });
    
    return keysToRemove.length;
  }

  // Get detailed storage analysis
  static analyzeStorage() {
    let totalSize = 0;
    const itemAnalysis = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      const size = value ? value.length : 0;
      totalSize += size;
      
      itemAnalysis.push({
        key,
        sizeKB: (size / 1024).toFixed(1),
        sizeBytes: size
      });
    }
    
    return {
      totalSizeKB: (totalSize / 1024).toFixed(1),
      totalSizeBytes: totalSize,
      itemCount: localStorage.length,
      items: itemAnalysis.sort((a, b) => b.sizeBytes - a.sizeBytes)
    };
  }
}